import React, { Suspense, lazy } from 'react'
import PropTypes from 'prop-types'
import { Input, Button, Loader } from 'components/UIElements'
import { urlRegex, INSTRUMENT_TYPE_MAP, MODAL_CONTENT_MAP, MODAL_BUTTONS_MAP, MODAL_CLASSES_MAP } from 'utils/constants'
import SurveyToolbar from './SurveyToolbar'

const ItemList = lazy(() => import(/* webpackChunkName: "dnd-kit-survey" */ './ItemList'))
import ItemEditor from './ItemEditor/ItemEditor'
import '../stylesheets/Survey.scss'
import { getRandomQuestionId } from '../../../../../../../utils/getRandomQuestionId'

export default class SurveyView extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = { activeItemId: null }
  }

  componentDidMount() {
    const { survey, addItem } = this.props
    if (survey.order[0].length === 0) {
      const newQId = getRandomQuestionId()
      addItem({
        itemType: 'introduction',
        index: 0,
        newQId,
      })
      this.setState({ activeItemId: newQId })
    } else {
      this.setState({ activeItemId: survey.order[0][0] })
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.id !== prevProps.id) {
      this.setState({ activeItemId: this.props.survey.order[0][0] })
    }
  }

  onMoveItem = ({ oldIndex, newIndex }) => {
    if (newIndex !== 0) {
      const currentQuestionId = this.props.survey.order[0][oldIndex]
      const { sibling_id } = this.props.survey.questions[currentQuestionId]
      const selectedItemsArr = sibling_id ? this.props.survey.siblings?.[sibling_id] : null
      this.props.moveItem(this.props.survey, oldIndex, newIndex, selectedItemsArr)
    }
  }

  onDeleteItem = itemId => {
    return () => {
      this.props.deleteItem(this.props.survey, itemId)
    }
  }

  onChangeItemType = (itemId, prevType, nextType) => {
    this.props.changeItemType(this.props.survey, itemId, prevType, nextType)
  }

  onDeleteChoice = (itemId, choiceId) => {
    return () => {
      this.props.deleteChoice(this.props.survey, itemId, choiceId)
    }
  }

  onAddItem = (itemType, index) => {
    const { addItem } = this.props
    return e => {
      const newQId = getRandomQuestionId()
      addItem({
        itemType,
        index,
        newQId,
      })
      this.setState({ activeItemId: newQId })
    }
  }

  onToggleEdit = itemId => {
    if (this.props.activeItemId !== itemId) {
      this.setState({ activeItemId: itemId })
    }
  }

  setActiveItemId = itemId => {
    this.setState({ activeItemId: itemId })
  }

  onRandomize = () => {
    const { openModal, randomizeQuestions } = this.props
    openModal({
      content: MODAL_CONTENT_MAP.shuffleQuestionOrder,
      confirmButton: MODAL_BUTTONS_MAP.proceed,
      cancelButton: MODAL_BUTTONS_MAP.cancel,
      className: MODAL_CLASSES_MAP.confirmation,
      onConfirm: randomizeQuestions,
    })
  }

  shouldCancelStart(e) {
    return e.target.className !== 'grab-icon'
  }

  onUpdateURL = value => {
    const { updateArticleURL } = this.props
    updateArticleURL(value.trim())
  }

  onUpdateArticleInstrumentTitle = e => {
    const { updateInstrumentTitle, updateArticleTitle } = this.props
    updateArticleTitle(e)
    updateInstrumentTitle(e)
  }

  render() {
    const {
      article,
      downloadInstrument,
      duplicateItem,
      errors,
      instrumentTitle,
      isLoading,
      onSave,
      studyID,
      survey,
      type,
      updateTitle,
      version,
      wordCount,
      study,
      userId,
      isReadOnlyState,
      reviewScreen,
      toggleAllItemAttribute,
      updateMetadataField,
    } = this.props
    const { activeItemId } = this.state
    const { title, id, questions } = survey
    const isArticle = type === INSTRUMENT_TYPE_MAP.article
    const articleHasSpaces = article.url.includes(' ') && errors.URLError
    const articleIsBlank = !article.url && errors.blankURLError
    const articleIsInvalid = !article.url.match(urlRegex) && errors.invalidUrlError

    return (
      <div className='survey-builder'>
        <SurveyToolbar
          studyID={studyID}
          id={id}
          version={version}
          onRandomize={this.onRandomize}
          wordCount={wordCount}
          type={type}
          isLoading={isLoading}
          updateTitle={isArticle ? this.onUpdateArticleInstrumentTitle : updateTitle}
          title={isArticle ? instrumentTitle : title}
          errors={errors}
          download={downloadInstrument}
          onSave={onSave}
          questions={questions}
          isReadOnlyState={isReadOnlyState}
          updateMetadataField={updateMetadataField}
          reviewScreen={reviewScreen}
          toggleAllItemAttribute={toggleAllItemAttribute}
        />
        <div className={`builder-content${!isArticle && activeItemId ? ' item-editor-open' : ''}`}>
          {isArticle ? (
            <div className='article-builder'>
              <div className='survey-item-container'>
                <p className='label-small'>ARTICLE LINK</p>
                <Input
                  id='article-link'
                  hasError={articleHasSpaces || articleIsBlank || articleIsInvalid}
                  placeholder={errors.blankURLError || 'Enter URL of article here'}
                  onChange={value => this.onUpdateURL(value)}
                  value={article.url}
                  errorText={errors.URLError || errors.invalidUrlError}
                  disabled={isReadOnlyState}
                />
              </div>
              <div className='button-list'>
                <Button id='next' content='Next' onClick={() => onSave('properties')} />
              </div>
            </div>
          ) : (
            <div>
              <div className='main-editor-section'>
                <Suspense fallback={<div className='flexed center-justified' style={{ minHeight: 120 }}><Loader inContainer size={24} /></div>}>
                  <ItemList
                    {...this.props}
                    activeItemId={activeItemId}
                    onSortEnd={this.onMoveItem}
                    shouldCancelStart={this.shouldCancelStart}
                    lockToContainerEdges
                    helperClass='sortableHelper'
                    lockAxis='y'
                    onToggleEdit={this.onToggleEdit}
                    onDeleteItem={this.onDeleteItem}
                    onChangeItemType={this.onChangeItemType}
                    onDeleteChoice={this.onDeleteChoice}
                    onAddItem={this.onAddItem}
                  />
                </Suspense>
              </div>
              <div className={`right-section${activeItemId ? ' active' : ''}`}>
                {survey.questions[activeItemId] && (
                  <ItemEditor
                    key={activeItemId}
                    survey={survey}
                    question={survey.questions[activeItemId]}
                    activeItemId={activeItemId}
                    setActiveItemId={this.setActiveItemId}
                    changeItemType={this.onChangeItemType}
                    duplicateItem={duplicateItem}
                    onDeleteItem={this.onDeleteItem}
                    study={study}
                    userId={userId}
                    isReadOnlyState={isReadOnlyState}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
}

SurveyView.propTypes = {
  activeItemId: PropTypes.string,
  addChoiceOnPaste: PropTypes.func,
  addItem: PropTypes.func,
  changeItemType: PropTypes.func,
  deleteChoice: PropTypes.func,
  deleteItem: PropTypes.func,
  duplicateItem: PropTypes.func,
  initializeBlankSurvey: PropTypes.func,
  instrumentTitle: PropTypes.string,
  moveItem: PropTypes.func,
  noQuestionsErr: PropTypes.string,
  openModal: PropTypes.func,
  randomizeQuestions: PropTypes.func,
  studyID: PropTypes.number,
  survey: PropTypes.object,
  titleErr: PropTypes.string,
  toggleEdit: PropTypes.func,
  updateArticleTitle: PropTypes.func,
  updateArticleURL: PropTypes.func,
  updateInstrumentTitle: PropTypes.func,
  updateIntrumentTitle: PropTypes.func,
  updateTitle: PropTypes.func,
  url: PropTypes.string,
  version: PropTypes.number,
  versionOptions: PropTypes.array,
  wordCount: PropTypes.number,
}
